import React, { useState, useEffect } from 'react';
import { useConfiguracion } from '@/context/ConfiguracionContext';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import * as configuracionApiService from '@/services/configuracionApiService';
import * as sucursalesApiService from '@/services/sucursalesApiService';
import toastService from '@/utils/toastService';
import Button from '@/components/common/Button';
import Loader from '@/components/common/Loader';
import Modal from '@/components/common/Modal';
import SucursalList from './SucursalList';
import SucursalForm from './SucursalForm';
import ConfiguracionForm from './ConfiguracionForm';
import LogoMantenedor from "./LogoMantenedor";
import GestionFestivos from "./GestionFestivos";
import { FaBuilding, FaPlus, FaPen, FaTrash, FaCog, FaCalendarAlt, FaImage } from 'react-icons/fa';

/**
 * Componente para gestionar la configuración general del taller y las sucursales
 */
const ConfiguracionMantenedor = () => {
  // Estado para la configuración general  
  const { configuracion, loading: configLoading } = useConfiguracion();
  const { empresaActual } = useSimpleEmpresa();
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Estado para las sucursales
  const [sucursales, setSucursales] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estado para la tabla de configuración
  const [configuraciones, setConfiguraciones] = useState([]);
  const [loadingConfiguraciones, setLoadingConfiguraciones] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [editingSucursalId, setEditingSucursalId] = useState(null);
  
  // Estado para las pestañas
  const [activeTab, setActiveTab] = useState('sucursales');

  
  // Cargar sucursales y configuraciones al iniciar
  useEffect(() => {
    if (empresaActual?.id) {
      setLoading(true);
      fetchSucursales();
      fetchConfiguraciones();
    }
  }, [empresaActual?.id]);
  
  // Función para cargar sucursales
  const fetchSucursales = async () => {
    setLoadingSucursales(true);
    try {
      // Cargar solo sucursales de la empresa actual
      const data = await sucursalesApiService.getSucursales(empresaActual?.id);
      setSucursales(data);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
      toastService.error('Error al cargar las sucursales');
    } finally {
      setLoadingSucursales(false);
    }
  };
  
  // Función para cargar configuraciones
  const fetchConfiguraciones = async () => {
    setLoadingConfiguraciones(true);
    try {
      // Solo cargar configuraciones editables por el taller de la empresa actual
      const data = await configuracionApiService.getConfiguracionEditablePorTaller(empresaActual?.id);
      setConfiguraciones(data);
    } catch (error) {
      console.error('Error al cargar configuraciones:', error);
      toastService.error('Error al cargar las configuraciones');
    } finally {
      setLoadingConfiguraciones(false);
      setLoading(false);
    }
  };


  
  // Abrir modal para crear nueva sucursal
  const handleNewSucursal = () => {
    setEditingSucursalId(null);
    setModalOpen(true);
  };
  
  // Abrir modal para editar sucursal
  const handleEditSucursal = (sucursal) => {
    setEditingSucursalId(sucursal.id);
    setModalOpen(true);
  };
  
  // Abrir modal para crear nueva configuración
  const handleNewConfig = () => {
    setEditingConfigId(null);
    setConfigModalOpen(true);
  };
  
  // Abrir modal para editar configuración
  const handleEditConfig = (config) => {
    setEditingConfigId(config.id);
    setConfigModalOpen(true);
  };
  
  // Eliminar configuración
  const handleDeleteConfig = async (clave) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta configuración?')) {
      return;
    }
    
    setLoadingConfiguraciones(true);
    try {
      await configuracionApiService.deleteConfiguracion(clave, empresaActual?.id);
      toastService.success('Configuración eliminada correctamente');
      fetchConfiguraciones();
    } catch (error) {
      console.error('Error al eliminar configuración:', error);
      toastService.error('Error al eliminar la configuración');
    } finally {
      setLoadingConfiguraciones(false);
    }
  };
  
  // Guardar configuración (crear o actualizar)
  const handleConfigSubmit = async (formData) => {
    // Validar campos requeridos
    if (!formData.clave || !formData.valor) {
      toastService.error('La clave y el valor son obligatorios');
      return;
    }

    setLoadingConfiguraciones(true);
    try {
      // Para edición, mantener el editablePorTaller existente
      // Para creación, establecer como true (ya que solo se crean configuraciones editables desde esta interfaz)
      const editablePorTaller = editingConfigId ? 
        (configuraciones.find(c => c.id === editingConfigId)?.editable_por_taller ?? true) : 
        true;

      await configuracionApiService.setConfiguracion({
        clave: formData.clave,
        valor: formData.valor,
        tipo: formData.tipo,
        descripcion: formData.descripcion,
        sucursal_id: null, // null para configuración global
        editable_por_taller: editablePorTaller,
        empresa_id: empresaActual?.id
      });
      
      toastService.success(editingConfigId ? 'Configuración actualizada correctamente' : 'Configuración creada correctamente');
      
      // Recargar datos y cerrar modal
      fetchConfiguraciones();
      setConfigModalOpen(false);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      toastService.error('Error al guardar la configuración');
    } finally {
      setLoadingConfiguraciones(false);
    }
  };
  
  // Estado para el modal de confirmación de eliminación
  const [deletingSucursalId, setDeletingSucursalId] = useState(null);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  // Mostrar modal de confirmación para eliminar sucursal
  const handleDeleteSucursal = (id) => {
    setDeletingSucursalId(id);
    setConfirmDeleteModalOpen(true);
  };

  // Confirmar eliminación de sucursal
  const confirmDeleteSucursal = async () => {
    if (!deletingSucursalId) return;
    
    setLoadingSucursales(true);
    try {
      await sucursalesApiService.deleteSucursal(deletingSucursalId);
      toastService.success('Sucursal eliminada correctamente');
      fetchSucursales();
    } catch (error) {
      console.error('Error al eliminar sucursal:', error);
      toastService.error('Error al eliminar la sucursal');
    } finally {
      setLoadingSucursales(false);
      setConfirmDeleteModalOpen(false);
      setDeletingSucursalId(null);
    }
  };
  
  // Guardar sucursal (crear o actualizar)
  const handleSucursalSubmit = async (formData) => {
    // Validar campos requeridos
    if (!formData.Nombre) {
      toastService.error('El nombre de la sucursal es obligatorio');
      return;
    }

    setLoadingSucursales(true);
    try {
      // Preparar datos para envío, enviando los horarios como objeto JSON
      const dataToSend = {
        ...formData,
        horarios: formData.horarios || null
      };

      if (editingSucursalId) {
        // Actualizar sucursal existente
        await sucursalesApiService.updateSucursal(editingSucursalId, dataToSend);
        toastService.success('Sucursal actualizada correctamente');
      } else {
        // Crear nueva sucursal para la empresa actual
        await sucursalesApiService.createSucursal(dataToSend, empresaActual?.id);
        toastService.success('Sucursal creada correctamente');
      }
      
      // Recargar datos y cerrar modal
      fetchSucursales();
      setModalOpen(false);
    } catch (error) {
      console.error('Error al guardar sucursal:', error);
      toastService.error('Error al guardar la sucursal');
    } finally {
      setLoadingSucursales(false);
    }
  };

  // Definir las pestañas disponibles
  const tabs = [
    {
      id: 'sucursales',
      label: 'Sucursales',
      icon: FaBuilding,
      color: 'blue'
    },
    {
      id: 'festivos',
      label: 'Festivos y Fechas',
      icon: FaCalendarAlt,
      color: 'purple'
    },
    {
      id: 'logo',
      label: 'Logo',
      icon: FaImage,
      color: 'green'
    },
    {
      id: 'configuraciones',
      label: 'Configuraciones',
      icon: FaCog,
      color: 'yellow'
    }
  ];

  // Mostrar loading si la empresa está cargando
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Cargando datos...</p>
        </div>
      </div>
    );
  }

  // Función para renderizar el contenido de cada pestaña
  const renderTabContent = () => {
    switch (activeTab) {
      case 'sucursales':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Sucursales</h2>
              <Button
                variant="primary"
                onClick={handleNewSucursal}
                className="px-3 py-1.5 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 rounded"
              >
                <FaPlus size={14} /> Nueva Sucursal
              </Button>
            </div>
            <SucursalList 
              sucursales={sucursales} 
              loading={loadingSucursales} 
              onEdit={handleEditSucursal} 
              onDelete={handleDeleteSucursal} 
              onNew={handleNewSucursal} 
            />
          </div>
        );
      
      case 'festivos':
        return (
          <div>
            <GestionFestivos />
          </div>
        );
      
      case 'logo':
        return (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">Configuración del Logo</h2>
            <LogoMantenedor />
          </div>
        );
      
      case 'configuraciones':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Configuración del Sistema</h2>
              <Button
                variant="primary"
                onClick={handleNewConfig}
                className="px-4 py-2 flex items-center gap-2 bg-green-600 hover:bg-green-700 rounded"
              >
                <FaPlus size={14} /> Nueva Configuración
              </Button>
            </div>
            
            {loadingConfiguraciones ? (
              <div className="flex justify-center items-center h-32">
                <Loader size="md" color="primary" />
              </div>
            ) : (
              <div>
                {configuraciones.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <FaCog className="mx-auto mb-3 text-3xl" />
                    <p>No hay configuraciones definidas</p>
                    <p className="text-sm mt-2">Agrega configuraciones como nombre_empresa, direccion_empresa, etc.</p>
                  </div>
                ) : (
                  <div className={isMobile ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"}>
                    {configuraciones.map((config) => (
                      <div key={config.id} className="bg-gray-700 rounded border border-gray-600 hover:border-gray-500 transition-colors">
                        {isMobile ? (
                          // Versión móvil - con botones grandes con texto
                          <div>
                            <div className="p-4">
                              <div className="flex-1 mb-3">
                                <div className="font-medium text-white text-sm mb-1">{config.clave}</div>
                                <div className="text-xs text-gray-400">{config.descripcion}</div>
                              </div>
                              <div className="text-xs mb-2">
                                <span className="text-gray-400">Valor:</span>
                                <div className="text-white mt-1 p-2 bg-gray-800 rounded text-xs font-mono">
                                  {config.tipo === 'json' 
                                    ? JSON.stringify(JSON.parse(config.valor), null, 2).substring(0, 80) + (config.valor.length > 80 ? '...' : '')
                                    : config.valor.length > 40 
                                      ? config.valor.substring(0, 40) + '...'
                                      : config.valor
                                  }
                                </div>
                              </div>
                              <div className="text-xs">
                                <span className="inline-block bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs">
                                  {config.tipo}
                                </span>
                              </div>
                            </div>
                            {/* Botones móviles - como en otros mantenedores */}
                            <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                              <button
                                onClick={() => handleEditConfig(config)}
                                className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                                title="Editar"
                              >
                                <FaPen className="text-lg" />
                                <span className="text-xs mt-1">Editar</span>
                              </button>
                              <button
                                onClick={() => handleDeleteConfig(config.clave)}
                                className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                                title="Eliminar"
                              >
                                <FaTrash className="text-lg" />
                                <span className="text-xs mt-1">Eliminar</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Versión escritorio - con botones cuadrados pequeños
                          <div className="p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="font-medium text-white text-sm mb-1">{config.clave}</div>
                                <div className="text-xs text-gray-400">{config.descripcion}</div>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <button
                                  onClick={() => handleEditConfig(config)}
                                  className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded"
                                  title="Editar"
                                >
                                  <FaPen />
                                </button>
                                <button
                                  onClick={() => handleDeleteConfig(config.clave)}
                                  className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                                  title="Eliminar"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                            <div className="text-xs mb-2">
                              <span className="text-gray-400">Valor:</span>
                              <div className="text-white mt-1 p-2 bg-gray-800 rounded text-xs font-mono">
                                {config.tipo === 'json' 
                                  ? JSON.stringify(JSON.parse(config.valor), null, 2).substring(0, 80) + (config.valor.length > 80 ? '...' : '')
                                  : config.valor.length > 40 
                                    ? config.valor.substring(0, 40) + '...'
                                    : config.valor
                                }
                              </div>
                            </div>
                            <div className="text-xs">
                              <span className="inline-block bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs">
                                {config.tipo}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="w-full px-4 py-6">
      <h1 className="text-2xl font-bold text-white mb-5">Configuración del Taller</h1>
      
      <div className="mb-6 bg-blue-900/30 rounded-lg border border-blue-800/50">
        <p className="p-3 text-sm text-blue-300">
          Gestiona la configuración de tu taller desde esta página. Usa las pestañas para navegar entre diferentes secciones.
        </p>
      </div>
      
      {/* Pestañas de navegación */}
      <div className="mb-6">
        <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'flex-row space-x-1'} bg-gray-800 rounded-lg p-1`}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 flex-1
                  ${isActive 
                    ? tab.id === 'sucursales' 
                      ? 'bg-blue-600 text-white shadow-lg'
                      : tab.id === 'festivos'
                        ? 'bg-purple-600 text-white shadow-lg'
                        : tab.id === 'logo'
                          ? 'bg-green-600 text-white shadow-lg'
                          : 'bg-yellow-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }
                  ${isMobile ? 'justify-center' : 'justify-center md:justify-start'}
                `}
              >
                <Icon className="text-lg" />
                <span className={`font-medium ${isMobile ? 'text-sm' : 'text-sm md:text-base'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Contenido de la pestaña activa */}
      <div className="min-h-[400px]">
        {renderTabContent()}
      </div>
      
      {/* Modal para crear/editar sucursal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingSucursalId ? 'Editar Sucursal' : 'Nueva Sucursal'}
        size="2xl"
      >
        <SucursalForm
          initialData={editingSucursalId ? sucursales.find(s => s.id === editingSucursalId) || {} : {}}
          onSubmit={handleSucursalSubmit}
          onCancel={() => setModalOpen(false)}
          loading={loadingSucursales}
        />
      </Modal>
        
      {/* Modal de confirmación para eliminar sucursal */}
      <Modal
        isOpen={confirmDeleteModalOpen}
        onClose={() => setConfirmDeleteModalOpen(false)}
        title="Confirmar eliminación"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setConfirmDeleteModalOpen(false)}
              className="px-4 py-2"
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={confirmDeleteSucursal}
              loading={loadingSucursales}
              className="px-6 py-2 flex items-center gap-2"
            >
              {!loadingSucursales && <FaTrash />}
              {loadingSucursales ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <p className="text-white mb-4">¿Estás seguro de que deseas eliminar esta sucursal?</p>
        <p className="text-gray-400 text-sm">Esta acción no se puede deshacer.</p>
      </Modal>
      
      {/* Modal para crear/editar configuración */}
      <Modal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        title={editingConfigId ? 'Editar Configuración' : 'Nueva Configuración'}
        size="2xl"
      >
        <ConfiguracionForm
          initialData={editingConfigId ? configuraciones.find(c => c.id === editingConfigId) || {} : {}}
          onSubmit={handleConfigSubmit}
          onCancel={() => setConfigModalOpen(false)}
          loading={loadingConfiguraciones}
        />
      </Modal>
    </div>
  );
};

export default ConfiguracionMantenedor;
